import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import CyberCubLogo from '../../components/CyberCubLogo.jsx';
import Tutor from '../../components/Tutor.jsx';
import MacDesktopArt from './MacDesktop.jsx';
import { useFinished } from '../../lib/practical.js';

/**
 * Practical site for "macOS CLI Basics".
 *
 * The third terminal, and the one that earns its place. A Mac shell is
 * a Unix shell, so most of it is the Linux lesson again - but three
 * things are genuinely its own, and all three matter:
 *
 *   zsh, not bash. The prompt ends in % and shows only the folder you
 *   are in, which throws anybody arriving from Linux.
 *
 *   No Terminal in the Dock. It has to be found with Spotlight, which
 *   is how a Mac user starts everything anyway.
 *
 *   The quarantine flag. Anything downloaded is stamped with one, and
 *   `xattr -d com.apple.quarantine` is the exact line scams tell people
 *   to paste. Meeting it here, with an explanation, is worth more than
 *   a paragraph about being careful.
 *
 * Everything is read-only, including the xattr that would strip the
 * flag - it explains itself and refuses.
 */

const STORE_KEY = 'cybercub.lab.macos-cli';

const USER = 'aarav';
const HOST = 'Cub-MacBook-Air';
const HOME = ['Users', USER];

const BANNER = [
  'Last login: Mon 16 Sep 09:14:02 on ttys000',
  '',
  'This shell is a practice one. Nothing you type here can change',
  'anything. Type help to see what it understands.',
  '',
];

/* ------------------------------------------------------------------ */
/* the machine                                                        */
/* ------------------------------------------------------------------ */

const DISK = {
  Users: {
    aarav: {
      Desktop: {},
      Documents: {
        'homework.pages': 'Volcanoes: a report.\nBy Aarav, class 6B.',
        'notes.txt':
          'Remember: the Trash is not deletion.\nAsk Cub about the hidden folder.',
        '.private': {
          'cub-code.txt':
            'Well done for finding this.\n\nA dot at the front of a name hides a file from Finder and from\nls. That is all it does. ls -a shows it, and so does anything\nreading the disk directly.\n\nIf something really must stay private it has to be encrypted.\nHiding is for tidiness.',
        },
      },
      Downloads: {
        'holiday.jpg': '(a picture - cat cannot show it)',
        'installer.dmg': '(a disk image - open it and it mounts under /Volumes)',
      },
      Library: { Preferences: {}, 'Application Support': {} },
      '.zshrc': '# ~/.zshrc - runs every time a shell opens\nalias ll="ls -alG"',
    },
    meera: {},
  },
  Applications: {
    'Safari.app': { Contents: { MacOS: { Safari: '(the program itself)' }, 'Info.plist': '(what the app says about itself)' } },
    'Calculator.app': { Contents: { MacOS: { Calculator: '(the program itself)' } } },
  },
  System: { Library: {} },
  Volumes: { 'Macintosh HD': {} },
  etc: {
    hosts:
      '##\n# Host Database\n##\n127.0.0.1       localhost\n255.255.255.255 broadcasthost\n\nThis file maps names to addresses before anything asks the internet.\nA line added here quietly sends a name somewhere else entirely, which\nis why it is worth knowing it exists.',
  },
  tmp: {},
};

/* Sealed against everybody, root included. That is the macOS idea the
   whole lesson is built around. */
const PROTECTED = ['/System', '/System/Library'];

/* Extended attributes. Only one matters here, and it is the reason
   this practical exists. */
const XATTRS = {
  '/Users/aarav/Downloads/installer.dmg': [
    'com.apple.quarantine: 0083;66e7a1c4;Safari;F3C1B2A0-4E11-4C7B-9D2E-7A11B0C4D5E6',
    'com.apple.metadata:kMDItemWhereFroms: https://downloads.example.invalid/installer.dmg',
  ],
};

const MODES = {
  '/Users/aarav/Downloads/installer.dmg': '-rw-r--r--@',
  '/Applications/Safari.app': 'drwxr-xr-x',
};

/* ------------------------------------------------------------------ */
/* path handling                                                      */
/* ------------------------------------------------------------------ */

const isFolder = (node) => node !== null && typeof node === 'object';

function nodeAt(parts) {
  let node = DISK;
  for (const part of parts) {
    if (!isFolder(node)) return null;
    if (!Object.prototype.hasOwnProperty.call(node, part)) return null;
    node = node[part];
  }
  return node;
}

const abs = (parts) => (parts.length ? `/${parts.join('/')}` : '/');

/** zsh's own prompt shows only the folder you are standing in. */
function short(parts) {
  if (parts.length === 2 && parts[0] === 'Users' && parts[1] === USER) return '~';
  return parts.length ? parts[parts.length - 1] : '/';
}

function resolve(cwd, raw) {
  const arg = raw.replace(/^["']|["']$/g, '');
  let parts;
  if (arg === '~' || arg.startsWith('~/')) parts = [...HOME, ...arg.slice(2).split('/')];
  else if (arg.startsWith('/')) parts = arg.split('/');
  else parts = [...cwd, ...arg.split('/')];

  const out = [];
  for (const p of parts.filter(Boolean)) {
    if (p === '.') continue;
    if (p === '..') out.pop();
    else out.push(p);
  }
  return out;
}

const isProtected = (parts) => PROTECTED.includes(abs(parts));

function walk(parts, node, out = []) {
  if (!isFolder(node)) return out;
  for (const [name, child] of Object.entries(node)) {
    const here = [...parts, name];
    out.push(here);
    if (isFolder(child)) walk(here, child, out);
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* the commands                                                       */
/* ------------------------------------------------------------------ */

const padStart = (s, n) => String(s).padStart(n, ' ');

function names(node, showHidden) {
  return Object.keys(node)
    .filter((n) => showHidden || !n.startsWith('.'))
    .sort((a, b) => a.localeCompare(b));
}

function listLong(parts, node, showHidden) {
  const list = names(node, showHidden);
  const lines = [`total ${list.length * 8}`];
  for (const name of list) {
    const child = node[name];
    const here = [...parts, name];
    const mode = MODES[abs(here)] ?? (isFolder(child) ? 'drwxr-xr-x' : '-rw-r--r--');
    const size = isFolder(child) ? 96 : String(child).length;
    lines.push(`${mode}  ${padStart(size, 5)} ${USER}  staff  16 Sep 09:14 ${name}`);
  }
  return lines;
}

const NET = [
  'lo0: flags=8049<UP,LOOPBACK,RUNNING,MULTICAST> mtu 16384',
  '\tinet 127.0.0.1 netmask 0xff000000',
  'en0: flags=8863<UP,BROADCAST,SMART,RUNNING,MULTICAST> mtu 1500',
  '\tether 9c:b6:d0:1f:3a:77',
  '\tinet 192.168.1.42 netmask 0xffffff00 broadcast 192.168.1.255',
  '',
  'A Mac has no ip command - it is ifconfig here, which is the older',
  'Unix spelling. en0 is the Wi-Fi, and 192.168.1.42 is this machine',
  'on the home network.',
];

const HELP = [
  'Commands you can use here:',
  '',
  '  pwd              print the folder you are in',
  '  ls               list this folder',
  '  ls -a            list it including hidden files (the dotted ones)',
  '  ls -l            list it with permissions, owner and size',
  '  ls -l@           the same, and shows which files carry extra flags',
  '  cd <folder>      move into it   (cd .. up, cd on its own goes home)',
  '  find . -name x   search this folder and everything under it',
  '  cat <file>       print what is inside a file',
  '  open <thing>     open it the way double-clicking would',
  '  whoami           which account am I?',
  '  id               which account, and which groups it is in',
  '  hostname         what is this machine called?',
  '  sw_vers          which version of macOS is this?',
  '  uname -a         which kernel is underneath?',
  '  ifconfig         what network is it on?',
  '  ps aux           what is running',
  '  lsof -i          what is talking to the network',
  '  csrutil status   is System Integrity Protection switched on?',
  '  xattr -l <file>  the hidden flags on a file, quarantine included',
  '  clear            clear the screen',
  '  exit             close the window',
  '',
  'Nothing here changes anything. It only ever looks.',
];

/* ------------------------------------------------------------------ */
/* the nine steps                                                     */
/* ------------------------------------------------------------------ */

const STEPS = [
  {
    id: 's1',
    title: 'Where am I?',
    ask: 'The zsh prompt shows only the folder name, not the whole path. Print the whole thing.',
    hint: 'pwd',
    done: (cmd) => /^pwd\s*$/.test(cmd),
  },
  {
    id: 's2',
    title: "What's around me?",
    ask: 'List what is in this folder.',
    hint: 'ls',
    done: (cmd) => /^ls\s*$/.test(cmd),
  },
  {
    id: 's3',
    title: 'Are there hidden files?',
    ask: 'ls does not show everything here, and neither does Finder. Ask it to.',
    hint: 'ls -a',
    done: (cmd) => /^ls\s+-\w*a/.test(cmd),
  },
  {
    id: 's4',
    title: 'Moving around the filesystem',
    ask: 'Move into the Documents folder.',
    hint: 'cd Documents',
    done: (cmd, { cwd }) => /^cd\s+\S/.test(cmd) && cwd[cwd.length - 1] === 'Documents',
  },
  {
    id: 's5',
    title: 'Finding the file on the disk',
    ask: 'Somewhere under here is a file called cub-code.txt. Search for it without knowing where it is.',
    hint: 'find . -name cub-code.txt',
    done: (cmd) => /^find\b/.test(cmd) && /cub-code/.test(cmd),
  },
  {
    id: 's6',
    title: 'Navigate to the file',
    ask: 'The search told you the folder. Go there.',
    hint: 'cd .private',
    done: (cmd, { cwd }) => /^cd\s+\S/.test(cmd) && cwd[cwd.length - 1] === '.private',
  },
  {
    id: 's7',
    title: 'Read the file',
    ask: 'Print what is inside it.',
    hint: 'cat cub-code.txt',
    done: (cmd) => /^cat\s+.*cub-code\.txt/.test(cmd),
  },
  {
    id: 's8',
    title: 'Name of the computer',
    ask: 'What is this machine actually called?',
    hint: 'hostname',
    done: (cmd) => /^(hostname|scutil\s+--get\s+ComputerName)\s*$/.test(cmd),
  },
  {
    id: 's9',
    title: 'How is this machine connected?',
    ask: 'Find its address on the network. Careful — the Linux command for this does not exist on a Mac.',
    hint: 'ifconfig',
    done: (cmd) => /^ifconfig\b/.test(cmd),
  },
];

const APPS = ['Safari', 'Mail', 'Notes', 'Calculator', 'System Settings', 'Photos'];

const WANTS_TERM = /^(term|terminal|terminal\.app|console|shell|zsh)/i;

/* ------------------------------------------------------------------ */

const load = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) ?? '{}'); } catch { return {}; }
};
const save = (v) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch { /* private window */ }
};

export default function MacCliLab() {
  const saved = useMemo(load, []);

  // 'closed' | 'open' | 'min' | 'max'
  const [win, setWin] = useState('closed');
  const [menu, setMenu] = useState(null); // null | 'spotlight' | 'launchpad'
  const [search, setSearch] = useState('');

  const [cwd, setCwd] = useState(HOME);
  const [lines, setLines] = useState(BANNER);
  const [input, setInput] = useState('');
  const [history, setHistory] = useState([]);
  const [hPos, setHPos] = useState(-1);
  const [doneSteps, setDoneSteps] = useState(saved.done ?? []);
  const [showHint, setShowHint] = useState(false);

  const screenRef = useRef(null);
  const inputRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => { document.title = 'macOS CLI Basics · CyberCub Lab'; }, []);
  useEffect(() => { save({ done: doneSteps }); }, [doneSteps]);

  useEffect(() => {
    const el = screenRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines, win]);

  useEffect(() => { if (menu === 'spotlight') searchRef.current?.focus(); }, [menu]);
  useEffect(() => {
    if (win === 'open' || win === 'max') inputRef.current?.focus();
  }, [win]);

  const showing = win === 'open' || win === 'max';
  const step = showing ? (STEPS.find((s) => !doneSteps.includes(s.id)) ?? null) : null;
  const allDone = doneSteps.length === STEPS.length;

  useFinished('/lab/macos-cli', allDone);

  const prompt = `${USER}@${HOST} ${short(cwd)} % `;

  const openTerm = () => {
    setMenu(null);
    setSearch('');
    setShowHint(false);
    setWin((w) => (w === 'max' ? 'max' : 'open'));
  };

  const closeTerm = () => {
    setWin('closed');
    setLines(BANNER);
    setCwd(HOME);
    setInput('');
    setHistory([]);
    setHPos(-1);
  };

  // Command and Space is how a Mac user opens anything at all. It only
  // arrives here if the machine you are actually sitting at does not
  // take it first, which is why the buttons matter more.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.metaKey && e.code === 'Space') {
        e.preventDefault();
        setMenu((m) => (m === 'spotlight' ? null : 'spotlight'));
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const run = (raw) => {
    const cmd = raw.trim();
    const out = [`${prompt}${raw}`];
    let nextCwd = cwd;

    if (!cmd) {
      setLines((l) => [...l, ...out]);
      return;
    }

    const [verb, ...rest] = cmd.split(/\s+/);
    const flags = rest.filter((r) => r.startsWith('-')).join('');
    const args = rest.filter((r) => !r.startsWith('-'));

    if (verb === 'clear') {
      setLines([]);
      setInput('');
      return;
    }
    if (verb === 'exit' || verb === 'logout') {
      closeTerm();
      return;
    }

    if (verb === 'help') out.push(...HELP);
    else if (verb === 'pwd') out.push(abs(cwd));
    else if (verb === 'whoami') out.push(USER);
    else if (verb === 'id') out.push(`uid=501(${USER}) gid=20(staff) groups=20(staff),80(admin)`);
    else if (verb === 'hostname' || verb === 'scutil') out.push(HOST);
    else if (verb === 'sw_vers') {
      out.push('ProductName:\t\tmacOS', 'ProductVersion:\t\t14.6.1', 'BuildVersion:\t\t23G93');
    } else if (verb === 'uname') {
      out.push(
        flags.includes('a')
          ? `Darwin ${HOST} 23.6.0 Darwin Kernel Version 23.6.0 arm64`
          : 'Darwin',
      );
    } else if (verb === 'ifconfig') out.push(...NET);
    else if (verb === 'ip') {
      out.push(
        'zsh: command not found: ip',
        '',
        'There is no ip command on a Mac. It is ifconfig here — the older',
        'Unix spelling that Linux has mostly moved on from.',
      );
    } else if (verb === 'ps') {
      out.push(
        'USER   PID  %CPU %MEM COMMAND',
        'root     1   0.0  0.3 /sbin/launchd',
        'aarav  412   0.4  1.8 /Applications/Safari.app/Contents/MacOS/Safari',
        'aarav  688   0.0  0.1 -zsh',
        'aarav  702   0.0  0.1 ps aux',
      );
    } else if (verb === 'lsof' || verb === 'netstat') {
      out.push(
        'COMMAND  PID  USER  NODE NAME',
        'Safari   412  aarav TCP  192.168.1.42:51244->142.250.185.78:443 (ESTABLISHED)',
        'rapportd 388  aarav TCP  *:49152 (LISTEN)',
        '',
        'LISTEN means something on this machine is waiting to be connected',
        'to. Every one of those is a door worth asking about, even on a Mac',
        'that arrived with it already open.',
      );
    } else if (verb === 'csrutil') {
      out.push(
        'System Integrity Protection status: enabled.',
        '',
        'That one word is why /System cannot be written to by anybody,',
        'root included. Turning it off needs a restart into recovery mode —',
        'which is deliberately awkward, because almost nobody should.',
      );
    } else if (verb === 'xattr') {
      // -d takes the attribute name first and the file second, so the
      // refusal has to come before any attempt to look a file up -
      // otherwise the attribute name gets treated as the filename.
      if (flags.includes('d')) {
        out.push(
          'xattr: this practice machine will not change anything.',
          '',
          'But look closely at what you just typed. That command strips the',
          'quarantine flag off a downloaded file, which is exactly what the',
          'warning box exists to show you.',
          '',
          'It is also the single most common line in scam instructions:',
          '"macOS says this app is damaged — just paste this to fix it."',
          'Nothing was damaged. You were being asked to switch off the check.',
        );
      } else {
        const target = resolve(cwd, args[0] ?? '');
        const list = XATTRS[abs(target)];
        if (nodeAt(target) === null) out.push(`xattr: ${args[0]}: No such file or directory`);
        else if (!list) out.push('');
        else if (flags.includes('l')) {
          out.push(
            ...list,
            '',
            'com.apple.quarantine is stamped on anything that arrived from the',
            'internet, along with which program downloaded it and when. It is',
            'what makes macOS ask "are you sure?" the first time you open it.',
          );
        } else out.push(...list.map((l) => l.split(':')[0]));
      }
    } else if (verb === 'open') {
      const target = args[0] ? resolve(cwd, args[0]) : cwd;
      if (nodeAt(target) === null) out.push(`The file ${args[0]} does not exist.`);
      else {
        out.push(
          `(opening ${abs(target)} the way a double-click would)`,
          '',
          'open is the bridge between the two halves of a Mac: it hands a',
          'file to whichever program owns it, exactly as Finder would.',
        );
      }
    } else if (verb === 'cd') {
      const target = args.length ? resolve(cwd, args[0]) : HOME;
      if (isFolder(nodeAt(target))) nextCwd = target;
      else if (nodeAt(target) !== null) out.push(`cd: not a directory: ${args[0]}`);
      else out.push(`cd: no such file or directory: ${args[0]}`);
    } else if (verb === 'ls') {
      const target = args.length ? resolve(cwd, args[0]) : cwd;
      const node = nodeAt(target);
      if (node === null) out.push(`ls: ${args[0]}: No such file or directory`);
      else if (!isFolder(node)) out.push(args[0]);
      else if (flags.includes('l')) {
        out.push(...listLong(target, node, flags.includes('a')));
        if (flags.includes('@')) {
          out.push(
            '',
            'The @ on the end of a permissions line means that file carries',
            'extra flags. Run xattr -l on it to see what they are.',
          );
        }
      } else {
        const list = names(node, flags.includes('a'));
        out.push(list.length ? list.join('   ') : '');
      }
    } else if (verb === 'cat') {
      const target = resolve(cwd, args[0] ?? '');
      if (isProtected(target)) out.push(`cat: ${abs(target)}: Operation not permitted`);
      else if (nodeAt(target) === null) out.push(`cat: ${args[0]}: No such file or directory`);
      else if (isFolder(nodeAt(target))) out.push(`cat: ${args[0]}: Is a directory`);
      else out.push(...String(nodeAt(target)).split('\n'));
    } else if (verb === 'find') {
      const where = args[0] && !args[0].startsWith('-') ? resolve(cwd, args[0]) : cwd;
      const nameIdx = rest.indexOf('-name');
      const term = nameIdx >= 0 ? (rest[nameIdx + 1] ?? '').replace(/["'*]/g, '') : '';
      const hits = walk(where, nodeAt(where))
        .filter((p) => !term || p[p.length - 1].includes(term))
        .map((p) => (args[0] === '.' || !args.length ? `./${p.slice(where.length).join('/')}` : abs(p)));
      out.push(...hits);
    } else if (verb === 'sudo') {
      out.push(
        `Password:`,
        '',
        'This practice machine will not run anything as an administrator,',
        'so there is no password to type. What matters is what you have',
        'just been asked for: sudo borrows administrator rights for one',
        'command, and typing your password in front of a command you did',
        'not expect hands over the whole machine.',
      );
    } else {
      out.push(
        `zsh: command not found: ${verb}`,
        '',
        'Type help to see what this shell understands.',
      );
    }

    out.push('');
    setLines((l) => [...l, ...out]);
    setCwd(nextCwd);
    setHistory((h) => [cmd, ...h].slice(0, 40));
    setHPos(-1);
    setShowHint(false);

    if (step && step.done(cmd, { cwd: nextCwd })) {
      setDoneSteps((d) => [...d, step.id]);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter') {
      run(input);
      setInput('');
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const n = Math.min(hPos + 1, history.length - 1);
      if (n >= 0) { setHPos(n); setInput(history[n]); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const n = hPos - 1;
      setHPos(n);
      setInput(n >= 0 ? history[n] : '');
    }
  };

  const task = showing
    ? step && { title: step.title, ask: step.ask, hint: step.hint }
    : {
        title: win === 'min' ? 'Bring the window back' : 'Open Terminal',
        ask:
          win === 'min'
            ? 'It has not closed — it is minimised. Its icon is still in the Dock with a dot under it.'
            : 'Terminal is not in the Dock on a Mac. Click the magnifying glass in the menu bar, type terminal, and open it — or find it in Launchpad.',
        hint:
          win === 'min'
            ? 'Click Terminal in the Dock'
            : 'Spotlight ▸ type terminal ▸ Terminal  (⌘ Space on a real Mac)',
      };

  const tutorSay = (() => {
    if (!showing) {
      return win === 'min'
        ? 'The window is only minimised, not closed. Its icon is in the Dock with a dot under it — click that and it comes straight back, with everything you typed still in it.'
        : 'Terminal is not in a Mac’s Dock, and that is not an oversight — almost nothing is, because Spotlight is how a Mac user starts everything. Click the magnifying glass at the top right, type terminal, and open it. On a real Mac, Command and Space does the same from anywhere.';
    }
    if (allDone) {
      return 'All nine, on the third machine. The prompt ends in a percent sign because a Mac runs zsh rather than bash, and the network command is the old spelling — but the five questions never changed, and by now you are not really learning commands any more. You are learning where to look.';
    }
    if (!step) return 'Type help to see everything this shell understands.';
    return `Step ${doneSteps.length + 1}. ${step.title}${/[?.!]$/.test(step.title) ? '' : '.'} ${step.ask} If you get stuck, press the hint button.`;
  })();

  return (
    <div className="lab">
      <header className="lab-top">
        <div className="row gap-sm">
          <CyberCubLogo size={38} />
          <div>
            <strong>macOS CLI Basics</strong>
            <span className="lab-sub">Practical · Ethical Hacker, Module 2</span>
          </div>
        </div>
        <Link to="/learn/lesson/cf5-macos-basics" className="btn btn-sm btn-ghost">
          ← Back to the lesson
        </Link>
      </header>

      <section className="lab-section">
        <div className="place-head">
          <div>
            <h2>Open Terminal</h2>
            <p className="lab-lead">
              A Mac desktop, and a shell you have to find yourself — it is not in the Dock. Then type
              a command, press Enter, read the answer. Nothing here changes anything.
            </p>
          </div>
          <span className="pill">{doneSteps.length} / {STEPS.length} steps</span>
        </div>

        <Tutor avatar="cub-bubble" say={tutorSay} />

        <ol className="cli-chips" aria-label="Steps">
          {STEPS.map((s, i) => {
            const done = doneSteps.includes(s.id);
            const now = step?.id === s.id;
            return (
              <li key={s.id} className={`${done ? 'is-done' : ''} ${now ? 'is-now' : ''}`}>
                <span className="cli-step-n">{done ? '✓' : i + 1}</span>
                <span className="cli-chip-label">{s.title}</span>
              </li>
            );
          })}
        </ol>

        {task && (
          <div className={`cli-task ${showing ? '' : 'is-opening'}`}>
            <div>
              <strong>{task.title}</strong>
              <p>{task.ask}</p>
            </div>
            {showHint ? (
              <code className="cli-hint">{task.hint}</code>
            ) : (
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setShowHint(true)}>
                Show me
              </button>
            )}
          </div>
        )}

        {allDone && showing && (
          <div className="cli-task is-done">
            <div>
              <strong>All nine done.</strong>
              <p>
                The shell stays open, and two commands here exist nowhere else. Try{' '}
                <code>csrutil status</code>, then <code>ls -l@ ~/Downloads</code> and{' '}
                <code>xattr -l ~/Downloads/installer.dmg</code>.
              </p>
            </div>
          </div>
        )}

        {/* ---------------- the desktop ---------------- */}
        <div className="win-desktop cli-desk">
          <MacDesktopArt running={win !== 'closed'} />

          {/* Spotlight, in the menu bar */}
          <button
            type="button"
            className="win-spot cli-hot"
            style={{ left: '73.5%', top: '0.3%', width: '4.5%', height: '4.7%' }}
            onClick={() => setMenu((m) => (m === 'spotlight' ? null : 'spotlight'))}
            aria-label="Spotlight search"
            aria-expanded={menu === 'spotlight'}
          />

          {/* Launchpad, in the Dock */}
          <button
            type="button"
            className="win-spot cli-hot"
            style={{ left: '47.2%', top: '87.6%', width: '6%', height: '9.5%' }}
            onClick={() => setMenu((m) => (m === 'launchpad' ? null : 'launchpad'))}
            aria-label="Launchpad"
            aria-expanded={menu === 'launchpad'}
          />

          {/* Terminal's own Dock icon, once it is running */}
          {win !== 'closed' && (
            <button
              type="button"
              className="win-spot cli-hot"
              style={{ left: '54.4%', top: '87.6%', width: '6%', height: '9.5%' }}
              onClick={() => setWin((w) => (w === 'min' ? 'open' : 'min'))}
              aria-label={win === 'min' ? 'Restore Terminal from the Dock' : 'Minimise Terminal'}
            />
          )}

          {/* ---------------- Spotlight / Launchpad ---------------- */}
          {menu && (
            <div className="cli-start is-mac" role="dialog" aria-label={menu === 'spotlight' ? 'Spotlight' : 'Launchpad'}>
              {menu === 'spotlight' ? (
                <>
                  <input
                    ref={searchRef}
                    className="cli-start-search is-mac"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && WANTS_TERM.test(search.trim())) openTerm();
                      if (e.key === 'Escape') setMenu(null);
                    }}
                    placeholder="Spotlight Search"
                    spellCheck="false"
                    autoCapitalize="none"
                    autoCorrect="off"
                    aria-label="Spotlight Search"
                  />

                  {search.trim() === '' ? (
                    <p className="cli-start-foot">
                      Type <code>terminal</code>. Spotlight searches programs, files and settings at
                      once, which is why a Mac needs so little in its Dock.
                    </p>
                  ) : WANTS_TERM.test(search.trim()) ? (
                    <>
                      <p className="cli-start-head">Top hit</p>
                      <button type="button" className="cli-start-hit" onClick={openTerm}>
                        <span className="cli-tb-tile">{'>_'}</span>
                        <span className="cli-start-hit-text">
                          <strong>Terminal</strong>
                          <em>Application — Utilities</em>
                        </span>
                      </button>
                    </>
                  ) : (
                    <p className="cli-start-none">
                      No results for “{search.trim()}”. Try <code>terminal</code>.
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="cli-start-head">Launchpad</p>
                  <div className="cli-start-grid">
                    {APPS.map((a) => (
                      <span key={a} className="cli-start-tile">{a}</span>
                    ))}
                    <button type="button" className="cli-start-tile is-hit" onClick={openTerm}>
                      Terminal
                    </button>
                  </div>
                  <p className="cli-start-foot">
                    Everything installed, on one screen. Terminal lives in the Utilities folder,
                    which is why it is easy to have used a Mac for years without meeting it.
                  </p>
                </>
              )}
            </div>
          )}

          {/* ---------------- the Terminal window ---------------- */}
          {showing && (
            <div className={`cli-win is-mac ${win === 'max' ? 'is-max' : ''}`}>
              <div className="cli-bar">
                <span className="mac-lights" aria-hidden="true">
                  <i className="is-red" /><i className="is-amber" /><i className="is-green" />
                </span>
                <span className="cli-bar-title is-centre">
                  {USER} — -zsh — 80×24
                </span>
                <button type="button" className="cli-win-btn" onClick={() => setWin('min')} aria-label="Minimise">
                  &#8211;
                </button>
                <button
                  type="button"
                  className="cli-win-btn"
                  onClick={() => setWin((w) => (w === 'max' ? 'open' : 'max'))}
                  aria-label={win === 'max' ? 'Restore down' : 'Maximise'}
                >
                  &#9633;
                </button>
                <button type="button" className="cli-win-btn is-close" onClick={closeTerm} aria-label="Close">
                  &#10005;
                </button>
              </div>

              <div
                className="cli-screen is-mac"
                ref={screenRef}
                onClick={() => inputRef.current?.focus()}
                role="presentation"
              >
                {lines.map((l, i) => (
                  <div key={i} className="cli-line">{l || ' '}</div>
                ))}

                <div className="cli-line cli-entry">
                  <span className="cli-prompt">{prompt}</span>
                  <input
                    ref={inputRef}
                    className="cli-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKey}
                    spellCheck="false"
                    autoCapitalize="none"
                    autoCorrect="off"
                    autoComplete="off"
                    aria-label="Type a command"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {allDone && (
          <div className="lab-done">
            <div>
              <strong>Three machines, one set of questions.</strong>
              <p style={{ margin: '0.3rem 0 0' }}>
                cd became pwd became pwd. dir became ls became ls. type became cat became cat. The
                third operating system took an afternoon because the thinking never changed — and
                that is the actual skill this module was for.
              </p>
            </div>
            <Link to="/learn/lesson/cf5-macos-basics" className="btn btn-sm">
              ← Back to the lesson
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
